import { LANGUAGES, PLAN_LIMITS, normalizeJoinCode, type LangCode } from "@1sky/shared";
import { useState, type FormEvent } from "react";
import { Link, useLocation } from "wouter";
import { AdSlot } from "../ads/AdSlot";
import { useAuth } from "../auth/session";
import { DepthCard } from "../landing/DepthCard";
import { SolidModel } from "../landing/SolidModel";
import { Starfield } from "../landing/Starfield";
import { accountsEnabled } from "../lib/config";
import { hubApi } from "../lib/hub";
import { loadPrefs, savePrefs } from "../lib/prefs";
import { pausedMessage, useServiceStatus } from "../lib/status";
import { Button } from "../ui/Button";
import { Page } from "../ui/Layout";
import { LanguageSelect } from "../ui/Field";

const spaces = [
  {
    id: "creatives",
    label: "The creative crew",
    title: "Make something worth staying up for.",
    body: "The late-night brainstorm. The first listen. The idea that gets better when someone else joins in. Bring your people, wherever they are.",
    detail: "Keep project names and creative terms in your room glossary.",
    image: "creative-crew",
    alt: "Young creatives sharing a sketchbook and making music together in an art studio",
    color: "lilac",
    tag: "Ideas welcome. Perfection optional.",
  },
  {
    id: "friends",
    label: "The just-one-more-game friends",
    title: "Same chaos. More of your favourite people.",
    body: "Squad up, catch up, or do absolutely nothing together. A place for the running jokes and the friends who turn a quick game into a whole evening.",
    detail: "Keep a browser call open alongside your game, with translated voice and chat.",
    image: "friends-hangout",
    alt: "Teenage friends laughing together with game controllers and headphones in a cozy living room",
    color: "lime",
    tag: "Good games. Better company.",
  },
  {
    id: "teams",
    label: "The small team with big plans",
    title: "Less getting lost in translation. More getting there.",
    body: "Your studio, shop, or growing business doesn't have to share a postcode or a first language. Talk through the next launch, client brief, or bright idea together.",
    detail: "Follow the live transcript and keep specialist terms in your room glossary.",
    image: "small-team",
    alt: "Small-business colleagues reviewing material swatches and talking over a laptop in their studio",
    color: "peach",
    tag: "Small team. Whole-world energy.",
  },
];

function Arrow({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={`h-5 w-5 ${className}`} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M5 12h14m-6-6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function Landing() {
  const [, navigate] = useLocation();
  const [lang, setLang] = useState<LangCode>(() => loadPrefs().lang);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<"start" | "join" | null>(null);
  const { profile } = useAuth();
  const paused = pausedMessage(useServiceStatus());

  async function start() {
    setBusy("start");
    setError("");
    try {
      savePrefs({ lang });
      navigate(`/r/${await hubApi.createRoom()}`);
    } catch {
      setError("We couldn't start a call just now. Please try again.");
    } finally {
      setBusy(null);
    }
  }

  async function join(e: FormEvent) {
    e.preventDefault();
    const normal = normalizeJoinCode(code);
    if (!normal) return setError("Codes are 9 characters, like K7M-2XP-9QT.");
    setBusy("join");
    setError("");
    const exists = await hubApi.roomExists(normal);
    setBusy(null);
    if (!exists) return setError("There's no call with that code. Check it with the person who invited you.");
    savePrefs({ lang });
    navigate(`/r/${normal}`);
  }

  return (
    <Page
      className="community-landing"
      headerRight={
        <>
          <a href="#spaces" className="hidden rounded-full px-4 py-2.5 hover:bg-soft sm:block">Find your space</a>
          <Link href="/pricing" className="rounded-full px-4 py-2.5 hover:bg-soft">Pricing</Link>
          {accountsEnabled && <Link href="/workspace" className="rounded-full border border-line px-4 py-2.5 hover:bg-soft">Sign in</Link>}
          <a href="#start" className="community-nav-cta">Let's hang</a>
        </>
      }
    >
      <Starfield />
      <section className="community-hero mx-auto grid max-w-6xl items-center gap-12 px-5 pt-12 pb-16 sm:px-6 sm:pt-20 lg:grid-cols-[1fr_1.05fr] lg:gap-10 lg:pb-24">
        <div>
          <p className="community-eyebrow"><span className="community-dot" /> Voice. Video. Good company.</p>
          <h1 className="mt-6 text-[clamp(3.15rem,5.8vw,5.1rem)] leading-[0.98] font-bold tracking-[-0.065em]">
            Your people.<br /><span className="community-lilac-text">Your language.</span>
          </h1>
          <p className="mt-7 max-w-md text-lg leading-relaxed text-muted">
            A place to hang, make, play and figure things out together. Bring your friends, creative crew or small team.
            Everyone speaks their language. Everyone feels part of it.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href="#start" className="community-primary-link">Find your people. Start a call. <Arrow /></a>
            <a href="#spaces" className="community-text-link">Take a look around <span aria-hidden>↗</span></a>
          </div>
          <p className="mt-4 text-xs text-muted">Free to start · No downloads · No account needed</p>
        </div>
        <div className="community-hero-art">
          <span className="community-art-note">a little less distance, a lot more connection.</span>
          <DepthCard className="community-photo-frame community-depth-photo">
            <img
              src="/images/creative-crew.jpg"
              alt="Creative friends making music and sharing ideas in a relaxed art studio"
              width="1536"
              height="1024"
              fetchPriority="high"
              className="h-full w-full object-cover"
            />
          </DepthCard>
          <p className="community-voice-note">More than a group chat.</p>
        </div>
      </section>

      <div className="community-ribbon" aria-label="Live group translation in voice, video, captions and chat">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-5 py-5 text-sm font-semibold sm:justify-between">
          <span>Speak freely.</span><span aria-hidden>✳</span><span>Hear everyone in your language.</span><span aria-hidden className="hidden sm:inline">✳</span><span>Live group translation.</span>
        </div>
      </div>

      <section id="spaces" aria-labelledby="spaces-title" className="community-model-scene community-anchor mx-auto max-w-6xl px-5 py-20 sm:px-6 sm:py-24">
        <SolidModel kind="headphones" background />
        <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
          <div><p className="community-eyebrow">However you come together</p><h2 id="spaces-title" className="mt-4 max-w-xl text-4xl leading-tight font-bold tracking-[-0.045em] sm:text-5xl">A little space for<br />your kind of people.</h2></div>
          <p className="max-w-xs text-base leading-relaxed text-muted">From a new side project to one more round. There's room for the whole group.</p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {spaces.map((space) => (
            <DepthCard as="article" key={space.id} id={space.id} className={`community-space-card community-${space.color}`}>
              <div className="community-card-image relative overflow-hidden">
                <img src={`/images/${space.image}.jpg`} alt={space.alt} width="1536" height="1024" loading="lazy" className="aspect-[4/3] w-full object-cover" />
                <span className="community-image-tag">{space.tag}</span>
              </div>
              <div className="community-card-body flex flex-1 flex-col p-6">
                <p className="text-xs font-semibold tracking-wide uppercase">{space.label}</p>
                <h3 className="mt-4 text-2xl leading-tight font-bold tracking-tight">{space.title}</h3>
                <p className="mt-4 text-sm leading-relaxed">{space.body}</p>
                <p className="mt-4 text-xs leading-relaxed opacity-75">{space.detail}</p>
                <a href="#start" className="mt-auto flex items-center justify-between gap-3 border-t border-current/20 pt-5 text-sm font-semibold"><span className="pt-5">Bring your {space.id === "creatives" ? "crew" : space.id === "friends" ? "friends" : "team"}</span><Arrow className="mt-5" /></a>
              </div>
            </DepthCard>
          ))}
        </div>
      </section>

      <section aria-labelledby="translation-title" className="community-model-scene community-translation mx-auto grid max-w-6xl items-center gap-12 px-5 py-10 sm:px-6 lg:grid-cols-2 lg:gap-20">
        <SolidModel kind="controller" background />
        <DepthCard className="community-conversation">
          <div className="mb-7 flex items-center justify-between gap-4 border-b border-line pb-4"><span className="font-semibold">The next big idea</span><span className="text-xs text-muted">Example conversation</span></div>
          <p className="text-xs text-muted">Lucía speaks Spanish</p>
          <p lang="es" className="mt-3 text-2xl font-semibold tracking-tight">¿Y si lo hacemos juntos?</p>
          <p className="mt-7 text-xs text-muted">Everyone else hears it their way</p>
          <div className="community-translation-bubble mt-3"><span className="community-avatar community-lilac" aria-hidden>A</span><div><p className="text-xs text-muted">Alex · English</p><p className="mt-1 text-sm">What if we do it together?</p></div></div>
          <div className="community-translation-bubble mt-3"><span className="community-avatar community-lime" aria-hidden>Y</span><div><p className="text-xs text-muted">Yuki · 日本語</p><p lang="ja" className="mt-1 text-sm">一緒にやってみない？</p></div></div>
          <p className="mt-6 text-xs text-muted">And when Alex or Yuki replies, it works the other way too.</p>
        </DepthCard>
        <div>
          <p className="community-eyebrow community-lilac-text">Live group translation</p>
          <h2 id="translation-title" className="mt-4 text-4xl leading-tight font-bold tracking-[-0.045em] sm:text-5xl">Same room.<br />Different languages.<br /><span className="community-lilac-text">Still your people.</span></h2>
          <p className="mt-6 text-base leading-relaxed text-muted">You shouldn't need a shared first language to share a good idea. Pick the language you speak and want to hear. 1sky translates each speaker for every listener, as the conversation happens.</p>
          <div className="mt-6 flex flex-wrap gap-2 text-xs"><span className="community-feature-pill">Translated voice</span><span className="community-feature-pill">Live captions</span><span className="community-feature-pill">Translated chat</span><span className="community-feature-pill">Room glossary</span></div>
          <details className="mt-7 text-sm text-muted">
            <summary className="cursor-pointer font-semibold text-ink">{LANGUAGES.length} languages. One conversation.</summary>
            <ul className="mt-4 flex flex-wrap gap-2">
              {LANGUAGES.map((language) => <li key={language.code} className="emoji-text rounded-full border border-line px-3 py-1.5">{language.flag} {language.native}</li>)}
            </ul>
          </details>
        </div>
      </section>

      <section id="start" aria-labelledby="start-title" className="community-model-scene community-anchor mx-auto max-w-6xl px-5 pt-20 pb-10 sm:px-6 sm:pt-24">
        <SolidModel kind="bubble" background />
        <DepthCard className="community-start-panel grid gap-10 p-6 sm:p-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:p-12">
          <div>
            <p className="community-eyebrow">Your next hangout starts here</p>
            <h2 id="start-title" className="mt-4 text-4xl font-bold tracking-[-0.045em] sm:text-5xl">Send the link.<br />Let the good<br />company in.</h2>
            <ol className="mt-7 space-y-3 text-sm text-muted">
              <li><span className="mr-3 font-semibold text-ink">01</span>Pick the language you speak and hear.</li>
              <li><span className="mr-3 font-semibold text-ink">02</span>Start a private call and share the link.</li>
              <li><span className="mr-3 font-semibold text-ink">03</span>Everyone joins in their own language.</li>
            </ol>
          </div>
          <div className="self-center">
            {paused && <p role="status" className="mb-5 rounded-2xl border border-line bg-soft px-5 py-4 text-sm">{paused}</p>}
            <div className="rounded-2xl border border-line bg-surface p-2">
              <LanguageSelect value={lang} onChange={setLang} label="Your language" className="border-0" />
              <Button size="lg" onClick={start} disabled={busy !== null || !!paused} className="mt-2 w-full justify-center">
                {busy === "start" ? "Starting…" : "Start a call"}<Arrow />
              </Button>
            </div>
            <p className="mt-3 text-center text-xs text-muted">Free to start. No account needed.</p>
            <form onSubmit={join} className="mt-7 flex items-end gap-2" noValidate>
              <label className="min-w-0 flex-1 text-sm">
                <span className="mb-2 block font-medium text-muted">Already invited? Enter a join code</span>
                <input
                  aria-label="Join code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="K7M-2XP-9QT"
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  className="h-12 w-full rounded-full border border-line bg-surface px-4 font-mono text-sm tracking-wider uppercase outline-none focus:border-ink"
                />
              </label>
              <Button type="submit" variant="secondary" disabled={busy !== null || !!paused} className="h-12 justify-center">Join</Button>
            </form>
            {error && <p role="alert" className="mt-3 text-sm font-medium text-[#ffabb3]">{error}</p>}
          </div>
        </DepthCard>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-8 sm:px-6">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-center">
          <div><h2 className="text-xl font-semibold">Good company doesn't need a big budget.</h2><p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted">Start free with translated captions, chat and {PLAN_LIMITS.free.voiceSeconds / 60} minutes of translated voice a month. Pro is ${PLAN_LIMITS.pro.priceUsd}/month for {PLAN_LIMITS.pro.voiceSeconds / 60} voice minutes, saved rooms and transcripts, and no ads.</p></div>
          <Link href="/pricing" className="community-text-link shrink-0">Find your plan <Arrow /></Link>
        </div>
        <div className="mt-8 max-w-sm"><AdSlot plan={profile?.plan} lang={lang} recentText={[]} placement="landing" /></div>
      </section>
    </Page>
  );
}
